import { Task, TaskUserState } from 'make-traffic-integration-core';
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GetPluginMetaByID } from 'app/components/management/common/plugins/plugin-metadata';
import { BodyText, FlexColumn, FlexRow, SvgIcon, TableData } from '@make-software/cspr-design';
import { TaskShortPreview } from 'app/components/management/apps/deals/view/task-short-preview';
import { StyledActionIconView } from 'app/components/common/table/styled-elements';
import {
  StyledCardRow,
  StyledChipsTag,
  StyledTableData
} from 'app/components/common/common-styles';
import { RewardsType } from 'app/console-api/apps/types';
import GripIcon from 'assets/icons/ic-grip.svg';
import {
  ButtonsContainer,
  StyledConfirmButton,
  StyledDismissButton
} from 'app/components/common/common-styles';
import {DealTradeChip} from 'app/components/common/deal-trade-type/deal-trade-chip';
import {TradeType} from 'app/console-api';
import { PluginChip } from 'app/components/management/common/plugins/plugin-chip/plugin-chip';

interface PreviewListItemProps {
  data: Task;
  rewardsTypes: RewardsType[];
  onView: (task: Task) => void;
  testUserID: string;
  handleGoButton: (task: Task) => void;
  handleClaimButton: (task: Task) => void;
}

const handleStyle: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  padding: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24
};

const userStateStatus = (state: TaskUserState): string => {
  if (state.isRewarded) {
    return 'Rewarded';
  }
  if (state.isCompleted) {
    return 'Completed';
  }
  if (state.isStarted) {
    return 'Started at ' + new Date(state.startedAt!).toLocaleString();
  }
  return 'Not Started';
};

export const PreviewListItem: React.FC<PreviewListItemProps> = ({
  data,
  rewardsTypes,
  onView,
  testUserID,
  handleGoButton,
  handleClaimButton
}) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners
  } = useSortable({ id: String(data.id) });

  const handleViewClick = () => {
    onView(data);
  };
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#f6f8fb' : undefined
  };

  const getRewardName = (type: string) => {
    const reward = rewardsTypes.find((reward) => reward.type === type);
    return reward ? reward.name : type;
  };

  const onClickGoButton = () => {
    handleGoButton(data);
  };
  const onClickClaimButton = () => {
    handleClaimButton(data);
  };

  return (
    <StyledCardRow ref={setNodeRef} key={data.id} style={style} $dragging={isDragging}>
      <TableData>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            className="row-handle"
            aria-label={`Drag ${data.name}`}
            title="Drag to reorder"
            style={handleStyle}
          >
            <SvgIcon src={GripIcon} />
          </button>
        </div>
      </TableData>
      <StyledTableData>
        <TaskShortPreview name={data.name} description={data.description} />
      </StyledTableData>
      <StyledTableData>
        <FlexColumn gap={8}>
          <FlexRow gap={8} align={'center'}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Task Type:
            </BodyText>
            <FlexRow align={'center'} gap={4}>
              <PluginChip id={data.plugin.id}></PluginChip>
            </FlexRow>
          </FlexRow>
            <FlexRow gap={8} align={'center'}>
              <BodyText size={3} scale={'sm'} variation={'darkGray'}>
                Deal Type:
              </BodyText>
              <DealTradeChip type={data.dealType as TradeType}></DealTradeChip>
            </FlexRow>
          <FlexRow gap={8} align={'center'}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Categories:
            </BodyText>
            <FlexRow gap={4} style={{ flexWrap: 'wrap' }}>
              {data.category.split(',').map((cat, idx) => (
                <StyledChipsTag key={idx}>
                  <BodyText size={3}>{cat}</BodyText>
                </StyledChipsTag>
              ))}
            </FlexRow>
          </FlexRow>
        </FlexColumn>
      </StyledTableData>
      <StyledTableData>
        <FlexColumn gap={8}>
          {data.rewards &&
            data.rewards.map((reward, index) => (
              <StyledChipsTag key={index} gap={8}>
                <BodyText size={3}>{reward.value + ' ' + getRewardName(reward.type)}</BodyText>
              </StyledChipsTag>
            ))}
        </FlexColumn>
      </StyledTableData>
      {testUserID !== '' && (
        <TableData>
          <BodyText size={3} scale={'sm'}>
            User state: {userStateStatus(data.userState)}
          </BodyText>
          <ButtonsContainer>
            <FlexRow gap={8}>
              <StyledConfirmButton onClick={onClickGoButton}>Go</StyledConfirmButton>
              <StyledDismissButton color={'secondaryBlue'} onClick={onClickClaimButton}>
                Claim
              </StyledDismissButton>
            </FlexRow>
          </ButtonsContainer>
        </TableData>
      )}
      <StyledTableData align="right">
        <StyledActionIconView onClick={handleViewClick} />
      </StyledTableData>
    </StyledCardRow>
  );
};
